import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui';
import FirmDocumentDialogSearchableSelect from 'pages/Documents/FirmDocuments/components/FirmDocumentDialogSearchableSelect';
import {
  FirmDocumentFolderCreateDialogField,
  FirmDocumentFolderCreateDialogFieldGroup,
  FirmDocumentFolderCreateDialogInput,
  FirmDocumentFolderCreateDialogLabel,
} from 'pages/Documents/FirmDocuments/components/FirmDocumentFolderCreateDialog/FirmDocumentFolderCreateDialog.styles';
import FirmDocumentFolderPathLabel from 'pages/Documents/FirmDocuments/components/FirmDocumentFolderPathLabel';
import { FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS } from './FirmDocumentFolderEditDialog.testIds';
import type { FirmDocumentFolderEditDialogProps } from './FirmDocumentFolderEditDialog.types';

const FOLDER_NAME_INPUT_ID = 'firm-documents-folder-edit-name';
const EMPTY_SIBLING_FOLDER_NAMES: string[] = [];
const EMPTY_SIBLING_FOLDER_NAMES_BY_TARGET_KEY: Record<string, string[]> = {};

const normalizeFolderName = (folderName: string) => folderName.trim().toLowerCase();
const getFolderEditTargetKey = (mdDocumentsId: number | null | undefined, parentFolderId: number | null) =>
  `${mdDocumentsId ?? 'missing'}:${parentFolderId ?? 'root'}`;

const getFolderNameHelperText = ({
  duplicateFolderName,
  folderName,
}: {
  duplicateFolderName: boolean;
  folderName: string;
}) => {
  if (!folderName.trim()) {
    return 'Folder name is required.';
  }

  if (duplicateFolderName) {
    return 'A folder with this name already exists in this location.';
  }

  return undefined;
};

const FirmDocumentFolderEditDialog = ({
  currentParentFolderId = null,
  folderName = null,
  isSaving = false,
  mdDocumentsId = null,
  onClose,
  onConfirm,
  open,
  parentFolderOptions,
  siblingFolderNamesByTargetKey = EMPTY_SIBLING_FOLDER_NAMES_BY_TARGET_KEY,
}: FirmDocumentFolderEditDialogProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [nextFolderName, setNextFolderName] = useState('');
  const [parentFolderId, setParentFolderId] = useState<number | null>(currentParentFolderId);
  const selectedParentFolderId = parentFolderId ?? null;
  const trimmedFolderName = nextFolderName.trim();
  const activeSiblingFolderNames = useMemo(
    () => siblingFolderNamesByTargetKey[
      getFolderEditTargetKey(mdDocumentsId, selectedParentFolderId)
    ] ?? EMPTY_SIBLING_FOLDER_NAMES,
    [mdDocumentsId, selectedParentFolderId, siblingFolderNamesByTargetKey]
  );
  const normalizedSiblingFolderNames = useMemo(
    () => new Set(activeSiblingFolderNames.map(normalizeFolderName)),
    [activeSiblingFolderNames]
  );
  const duplicateFolderName = Boolean(trimmedFolderName)
    && normalizedSiblingFolderNames.has(normalizeFolderName(trimmedFolderName));
  const helperText = getFolderNameHelperText({ duplicateFolderName, folderName: nextFolderName });
  const canSave = Boolean(trimmedFolderName && !helperText && !isSaving);
  const selectedParentOption = useMemo(
    () => parentFolderOptions.find(option => option.id === selectedParentFolderId),
    [parentFolderOptions, selectedParentFolderId]
  );

  const handleConfirm = useCallback(() => {
    if (!canSave) {
      return;
    }

    onConfirm({
      folderName: trimmedFolderName,
      parentFolderId: selectedParentFolderId,
    });
  }, [canSave, onConfirm, selectedParentFolderId, trimmedFolderName]);

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirm();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  // Reset the edit-folder form from the selected folder each time the modal opens.
  useEffect(() => {
    if (!open) {
      return;
    }

    setNextFolderName(folderName ?? '');
    setParentFolderId(currentParentFolderId);
  }, [currentParentFolderId, folderName, open]);

  // Move focus to the folder name input so rename can be completed from the keyboard.
  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus());
    }
  }, [open]);

  return (
    <Dialog
      data-testid={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.dialog}
      fullWidth
      maxWidth="xs"
      onClose={isSaving ? undefined : onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>Edit Folder</DialogTitle>
        <DialogCloseButton
          aria-label="Close edit folder dialog"
          data-testid={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.closeButton}
          disabled={isSaving}
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <DialogContent>
        <FirmDocumentFolderCreateDialogFieldGroup>
          <FirmDocumentDialogSearchableSelect
            clearSearchAriaLabel="Clear parent folder search"
            disabled={isSaving}
            emptyLabel="No folders found"
            itemLabelPlural="folders"
            itemLabelSingular="folder"
            label="Parent Folder"
            menuTestId={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.parentFolderMenu}
            onChange={setParentFolderId}
            options={parentFolderOptions}
            placeholder="Select Parent Folder"
            renderOptionLabel={option => <FirmDocumentFolderPathLabel label={option.label} />}
            searchPlaceholder="Find a Folder"
            searchTestId={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.parentFolderSearch}
            selectTestId={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.parentFolderSelect}
            selectedId={selectedParentFolderId}
            testIdForOption={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.parentOption}
          />
          <FirmDocumentFolderCreateDialogField>
            <FirmDocumentFolderCreateDialogLabel htmlFor={FOLDER_NAME_INPUT_ID}>
              Folder Name
            </FirmDocumentFolderCreateDialogLabel>
            <FirmDocumentFolderCreateDialogInput
              data-testid={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.folderNameInput}
              disabled={isSaving}
              error={Boolean(helperText)}
              helperText={helperText ?? ' '}
              id={FOLDER_NAME_INPUT_ID}
              inputRef={inputRef}
              onChange={event => setNextFolderName(event.target.value)}
              onKeyDown={handleNameKeyDown}
              placeholder="Enter folder name"
              value={nextFolderName}
            />
          </FirmDocumentFolderCreateDialogField>
        </FirmDocumentFolderCreateDialogFieldGroup>
      </DialogContent>
      <DialogActionFooter
        actionButtonTestId={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.saveButton}
        actionLabel="Edit Folder"
        cancelButtonTestId={FIRM_DOCUMENT_FOLDER_EDIT_DIALOG_TEST_IDS.cancelButton}
        isActionDisabled={!canSave || !selectedParentOption}
        isActionLoading={isSaving}
        isCancelDisabled={isSaving}
        onAction={handleConfirm}
        onCancel={onClose}
      />
    </Dialog>
  );
};

export default FirmDocumentFolderEditDialog;
